import { ReadonlyVec3, vec3 } from 'gl-matrix';
import { useEffect } from 'react';
import { CallbackInterface, useRecoilCallback, useRecoilValue } from 'recoil';
import { DicomRoi } from '~/domain/dicom/dicomRoi';
import {
    DicomContour,
    RoiGenerationAlgorithmType,
} from '~/domain/dicom/dicomstructureset';
import {
    domainEventBus,
    EventSource,
    RoiApprovalStatusChangeRequestedEvent,
    RoiColorChangeRequestedEvent,
    RoiContourModificationStartedEvent,
    RoiContoursModifiedEvent,
    RoiContourThicknessChangeRequestedEvent,
    RoiCreatedEvent,
    RoiCreationRequestedEvent,
    RoiDeletionRequestedEvent,
    RoiElectronDensityOverrideChangeRequestedEvent,
    RoiMassDensityOverrideChangeRequestedEvent,
    RoiDescriptionChangeRequestedEvent,
    RoiGenerationAlgorithmChangeRequestedEvent,
    RoiSelectionRequestedEvent,
    RoiInterpretedTypeChangeRequestedEvent,
    RoiNameChangeRequestedEvent,
    RoiPointCoordinatesChangeRequestedEvent,
    RoiPropertiesUpdatedEvent,
    RoiVisibilityChangeRequestedEvent,
    StructureSetApprovalChangeRequestedEvent,
    RoiCopyRequestedEvent,
    RoiSdfCopyOverRequestedEvent,
} from '~/domain/events';
import {
    getContourUndoService,
    getStructureSetRepository,
} from '~/domain/ServiceContainer';
import { generateUniqueStructureName } from '~/domain/services/dicomProcessor';
import { environmentState } from '~/state/atoms/engineAtoms';
import { hasUnsavedChangesState } from '~/state/atoms/patientAtoms';
import { selectedROIState } from '~/state/atoms/structureSet/roiAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import { generateDarkerColor } from '~/utils/color';

function updateRoiAndNotify(
    recoilInterface: CallbackInterface,
    structureSetUid: string | undefined,
    roiNumber: number,
    mutation: (roi: DicomRoi) => void,
    propertyUpdate: Partial<DicomRoi>,
    source: EventSource
) {
    if (!structureSetUid) {
        console.warn('No structure set UID available');
        return;
    }

    try {
        const repository = getStructureSetRepository();
        const { set } = recoilInterface;
        const updatedReadModel = repository.updateRoi(
            structureSetUid,
            roiNumber,
            mutation
        );
        set(currentStructureSetState, updatedReadModel);

        return domainEventBus.publish(
            new RoiPropertiesUpdatedEvent(roiNumber, propertyUpdate, source)
        );
    } catch (error) {
        console.error(`Failed to update ROI ${roiNumber}:`, error);
        throw error;
    }
}

function selectRoi(
    recoilInterface: CallbackInterface,
    structureSetUid: string | undefined,
    roiNumber: number
    // source: EventSource
) {
    if (!structureSetUid) {
        console.warn('No structure set UID available');
        return;
    }

    try {
        const repository = getStructureSetRepository();
        const { set } = recoilInterface;
        const model = repository.getReadModel(structureSetUid);

        if (!model.roiArray.some((roi) => roi.roiNumber === roiNumber)) {
            throw new Error(
                `ROI ${roiNumber} not found in StructureSet ${structureSetUid}`
            );
        }

        set(selectedROIState, roiNumber);

        // // could send a RoiSelectedEvent here if needed in future
        // return domainEventBus.publish(
        //     new RoiSelectedEvent(structureSetUid, roiNumber, source);
        // );
    } catch (error) {
        console.error(
            `Failed to select ROI ${roiNumber} from StructureSet ${structureSetUid}:`,
            error
        );
        throw error;
    }
}

/**
 * Application service layer for {@link DicomRoi} operations within {@link StructureSet}.
 *
 * Processes ROI-specific domain events by coordinating updates to individual ROIs
 * within the structure set.
 */
export function useRoiHandlers() {
    const structureSetReadModel = useRecoilValue(currentStructureSetState);
    const env = useRecoilValue(environmentState);

    const handleVisibilityChangeRequest = useRecoilCallback(
        (recoilInterface) =>
            async (event: RoiVisibilityChangeRequestedEvent) => {
                await updateRoiAndNotify(
                    recoilInterface,
                    structureSetReadModel?.dicomUid,
                    event.roiNumber,
                    (roi) => (roi.visible = event.visible),
                    { visible: event.visible },
                    event.source
                );
            },
        [structureSetReadModel?.dicomUid]
    );

    const handleNameChangeRequest = useRecoilCallback(
        (recoilInterface) => async (event: RoiNameChangeRequestedEvent) => {
            await updateRoiAndNotify(
                recoilInterface,
                structureSetReadModel?.dicomUid,
                event.roiNumber,
                (roi) => (roi.name = event.name),
                { name: event.name },
                event.source
            );
        },
        [structureSetReadModel?.dicomUid]
    );

    const handleColorChangeRequest = useRecoilCallback(
        (recoilInterface) => async (event: RoiColorChangeRequestedEvent) => {
            await updateRoiAndNotify(
                recoilInterface,
                structureSetReadModel?.dicomUid,
                event.roiNumber,
                (roi) => (roi.color = event.color),
                { color: event.color },
                event.source
            );
        },
        [structureSetReadModel?.dicomUid]
    );

    const handleContourThicknessChangeRequest = useRecoilCallback(
        (recoilInterface) =>
            async (event: RoiContourThicknessChangeRequestedEvent) => {
                await updateRoiAndNotify(
                    recoilInterface,
                    structureSetReadModel?.dicomUid,
                    event.roiNumber,
                    (roi) => (roi.contourThickness = event.contourThickness),
                    { contourThickness: event.contourThickness },
                    event.source
                );
            },
        [structureSetReadModel?.dicomUid]
    );

    const handleInterpretedTypeChangeRequest = useRecoilCallback(
        (recoilInterface) =>
            async (event: RoiInterpretedTypeChangeRequestedEvent) => {
                await updateRoiAndNotify(
                    recoilInterface,
                    structureSetReadModel?.dicomUid,
                    event.roiNumber,
                    (roi) => (roi.interpretedType = event.interpretedType),
                    { interpretedType: event.interpretedType },
                    event.source
                );
            },
        [structureSetReadModel?.dicomUid]
    );

    const handleElectronDensityOverrideChangeRequest = useRecoilCallback(
        (recoilInterface) =>
            async (event: RoiElectronDensityOverrideChangeRequestedEvent) => {
                await updateRoiAndNotify(
                    recoilInterface,
                    structureSetReadModel?.dicomUid,
                    event.roiNumber,
                    (roi) =>
                        (roi.electronDensityOverride =
                            event.electronDensityOverride),
                    { electronDensityOverride: event.electronDensityOverride },
                    event.source
                );
            },
        [structureSetReadModel?.dicomUid]
    );

    const handleMassDensityOverrideChangeRequest = useRecoilCallback(
        (recoilInterface) =>
            async (event: RoiMassDensityOverrideChangeRequestedEvent) => {
                await updateRoiAndNotify(
                    recoilInterface,
                    structureSetReadModel?.dicomUid,
                    event.roiNumber,
                    (roi) =>
                        (roi.massDensityOverride = event.massDensityOverride),
                    { massDensityOverride: event.massDensityOverride },
                    event.source
                );
            },
        [structureSetReadModel?.dicomUid]
    );

    const handleDescriptionChangeRequest = useRecoilCallback(
        (recoilInterface) =>
            async (event: RoiDescriptionChangeRequestedEvent) => {
                await updateRoiAndNotify(
                    recoilInterface,
                    structureSetReadModel?.dicomUid,
                    event.roiNumber,
                    (roi) => (roi.roiDescription = event.roiDescription),
                    { roiDescription: event.roiDescription },
                    event.source
                );
            },
        [structureSetReadModel?.dicomUid]
    );

    const handleGenerationAlgorithmChangeRequest = useRecoilCallback(
        (recoilInterface) =>
            async (event: RoiGenerationAlgorithmChangeRequestedEvent) => {
                await updateRoiAndNotify(
                    recoilInterface,
                    structureSetReadModel?.dicomUid,
                    event.roiNumber,
                    (roi) =>
                        (roi.roiGenerationAlgorithm =
                            event.roiGenerationAlgorithm),
                    { roiGenerationAlgorithm: event.roiGenerationAlgorithm },
                    event.source
                );
            },
        [structureSetReadModel?.dicomUid]
    );

    const handleApprovalStatusChangeRequest = useRecoilCallback(
        (recoilInterface) =>
            async (event: RoiApprovalStatusChangeRequestedEvent) => {
                // Handle the individual ROI approval status update
                await updateRoiAndNotify(
                    recoilInterface,
                    structureSetReadModel?.dicomUid,
                    event.roiNumber,
                    (roi) => (roi.approvalStatus = event.approvalStatus),
                    { approvalStatus: event.approvalStatus },
                    event.source
                );

                if (event.source === 'user') {
                    const { snapshot } = recoilInterface;
                    const updatedStructureSetReadModel =
                        await snapshot.getPromise(currentStructureSetState);

                    if (updatedStructureSetReadModel) {
                        // Need to check cascading status from ground truth
                        const repository = getStructureSetRepository();
                        const groundTruth = repository.getGroundTruth(
                            updatedStructureSetReadModel.dicomUid
                        );
                        const cascadingStatus =
                            groundTruth.getCascadingApprovalStatus();

                        if (cascadingStatus !== null) {
                            await domainEventBus.publish(
                                new StructureSetApprovalChangeRequestedEvent(
                                    updatedStructureSetReadModel.dicomUid,
                                    cascadingStatus,
                                    event.reviewerName,
                                    'system'
                                )
                            );
                        }
                    }
                }
            },
        [structureSetReadModel?.dicomUid]
    );

    const handleRoiCreationRequest = useRecoilCallback(
        (recoilInterface) => async (event: RoiCreationRequestedEvent) => {
            try {
                if (!structureSetReadModel?.dicomUid) {
                    console.warn(
                        'No structure set UID available for ROI creation'
                    );
                    return;
                }

                if (event.structureSetUid !== structureSetReadModel.dicomUid) {
                    console.warn(
                        `ROI creation event for structure set ${event.structureSetUid} ignored - current structure set is ${structureSetReadModel.dicomUid}`
                    );
                    return;
                }

                const repository = getStructureSetRepository();

                const { set } = recoilInterface;
                const { structureSetReadModel: createdReadModel, newRoi } =
                    repository.createRoi(
                        event.structureSetUid,
                        event.interpretedType
                    );
                let updatedReadModel = createdReadModel;

                // Place new point ROIs at the provided coordinates
                if (event.initialCoordinatesMm) {
                    const { x, y, z } = event.initialCoordinatesMm;
                    updatedReadModel = repository.updateRoi(
                        event.structureSetUid,
                        newRoi.roiNumber,
                        (roi) => {
                            roi.setPointCoordinates(vec3.fromValues(x, y, z));
                        }
                    );
                    const groundTruth = repository.getGroundTruth(
                        event.structureSetUid
                    );
                    groundTruth.dicomStructureSet.updateContourSequence(
                        newRoi.roiNumber
                    );
                }

                set(currentStructureSetState, updatedReadModel);
                set(hasUnsavedChangesState, true);

                // Emit ROI creation event for other systems (e.g., rendering engine)
                await domainEventBus.publish(
                    new RoiCreatedEvent(
                        event.structureSetUid,
                        newRoi.roiNumber,
                        event.source
                    )
                );

                selectRoi(
                    recoilInterface,
                    structureSetReadModel.dicomUid,
                    newRoi.roiNumber
                );
            } catch (error) {
                console.error(`Failed to handle ROI creation:`, error);
            }
        },
        [structureSetReadModel?.dicomUid]
    );

    const handleRoiSelectionRequest = useRecoilCallback(
        (recoilInterface) => async (event: RoiSelectionRequestedEvent) => {
            const currentSelection =
                await recoilInterface.snapshot.getPromise(selectedROIState);

            if (currentSelection === event.roiNumber) {
                recoilInterface.set(selectedROIState, -1);
                return;
            }

            if (!structureSetReadModel?.dicomUid) {
                console.warn(
                    'No structure set UID available for ROI selection'
                );
                return;
            }

            selectRoi(
                recoilInterface,
                structureSetReadModel.dicomUid,
                event.roiNumber
            );
        },
        [structureSetReadModel?.dicomUid]
    );

    const handleRoiDeletionRequest = useRecoilCallback(
        (recoilInterface) => async (event: RoiDeletionRequestedEvent) => {
            try {
                if (!structureSetReadModel?.dicomUid) {
                    console.warn(
                        'No structure set UID available for ROI deletion'
                    );
                    return;
                }

                if (event.structureSetUid !== structureSetReadModel.dicomUid) {
                    console.warn(
                        `ROI deletion event for structure set ${event.structureSetUid} ignored - current structure set is ${structureSetReadModel.dicomUid}`
                    );
                    return;
                }

                const repository = getStructureSetRepository();
                const groundTruth = repository.getGroundTruth(
                    event.structureSetUid
                );
                const roi = groundTruth.dicomStructureSet.rois.get(
                    event.roiNumber
                );
                if (!roi) {
                    console.warn(
                        `ROI ${event.roiNumber} not found in structure set ${event.structureSetUid}`
                    );
                    return;
                }

                const { set } = recoilInterface;
                const updatedReadModel = repository.deleteRoi(
                    event.structureSetUid, // Use event's structure set UID
                    event.roiNumber
                );
                set(currentStructureSetState, updatedReadModel);
                set(hasUnsavedChangesState, true);

                // Emit ROI deleted event for other systems (e.g., rendering engine)
                await domainEventBus.publish(
                    new RoiPropertiesUpdatedEvent(
                        event.roiNumber,
                        { deleted: true },
                        event.source
                    )
                );
            } catch (error) {
                console.error(
                    `Failed to handle ROI deletion for ROI ${event.roiNumber}:`,
                    error
                );
            }
        },
        [structureSetReadModel?.dicomUid]
    );

    const handleContoursModified = useRecoilCallback(
        (recoilInterface) => async (event: RoiContoursModifiedEvent) => {
            try {
                const structureSetUid = structureSetReadModel?.dicomUid;
                if (!structureSetUid) {
                    console.warn(
                        'No structure set UID available for contour modification'
                    );
                    return;
                }

                const currentStructureSet =
                    await recoilInterface.snapshot.getPromise(
                        currentStructureSetState
                    );
                const currentRoiReadModel = currentStructureSet?.roiArray.find(
                    (r) => r.roiNumber === event.roiNumber
                );

                const repository = getStructureSetRepository();
                const updatedReadModel = repository.updateRoi(
                    structureSetUid,
                    event.roiNumber,
                    (roi) => {
                        if (
                            roi.roiGenerationAlgorithm ===
                            RoiGenerationAlgorithmType.AUTOMATIC
                        ) {
                            roi.roiGenerationAlgorithm =
                                RoiGenerationAlgorithmType.SEMI_AUTOMATIC;
                        }
                    }
                );

                if (event.pixels && event.grid && currentRoiReadModel) {
                    const contourUndoService = getContourUndoService();

                    // Add initial-state first if this ROI has no undo history yet
                    if (
                        !contourUndoService.isRoiInitialized(event.roiNumber) &&
                        event.initialPixels &&
                        event.initialGrid
                    ) {
                        await contourUndoService.addContourOperation(
                            currentRoiReadModel,
                            event.initialPixels,
                            event.initialGrid,
                            'initial-state'
                        );
                    }

                    await contourUndoService.addContourOperation(
                        currentRoiReadModel,
                        event.pixels,
                        event.grid,
                        event.modificationType
                    );
                }

                recoilInterface.set(currentStructureSetState, updatedReadModel);
                recoilInterface.set(hasUnsavedChangesState, true);
            } catch (error) {
                console.error(
                    `Failed to handle contour modification for ROI ${event.roiNumber}:`,
                    error
                );
            }
        },
        [structureSetReadModel?.dicomUid]
    );

    const handlePointCoordinatesChangeRequest = useRecoilCallback(
        (recoilInterface) =>
            async (event: RoiPointCoordinatesChangeRequestedEvent) => {
                try {
                    const { x, y, z } = event.coordinates;
                    await updateRoiAndNotify(
                        recoilInterface,
                        structureSetReadModel?.dicomUid,
                        event.roiNumber,
                        (roi) => {
                            roi.setPointCoordinates(vec3.fromValues(x, y, z));
                        },
                        {},
                        event.source
                    );

                    // Persist updated point coordinates to the DICOM dataset
                    if (structureSetReadModel?.dicomUid) {
                        const repository = getStructureSetRepository();
                        const groundTruth = repository.getGroundTruth(
                            structureSetReadModel.dicomUid
                        );
                        groundTruth.dicomStructureSet.updateContourSequence(
                            event.roiNumber
                        );
                    }
                } catch (error) {
                    console.error(
                        `Failed to update point coordinates for ROI ${event.roiNumber}:`,
                        error
                    );
                }
            },
        [structureSetReadModel?.dicomUid]
    );

    const handleRoiCopyRequest = useRecoilCallback(
        (recoilInterface) => async (event: RoiCopyRequestedEvent) => {
            try {
                if (!structureSetReadModel?.dicomUid) {
                    console.warn('No structure set UID available for ROI copy');
                    return;
                }

                if (event.structureSetUid !== structureSetReadModel.dicomUid) {
                    console.warn(
                        `ROI copy event for structure set ${event.structureSetUid} ignored - current structure set is ${structureSetReadModel.dicomUid}`
                    );
                    return;
                }

                // Sync engine contour data to domain model so that
                // freshly drawn (unsaved) contours are available for copying
                env?.saveChanges();

                const repository = getStructureSetRepository();
                const groundTruth = repository.getGroundTruth(
                    event.structureSetUid
                );
                const sourceRoi = groundTruth.dicomStructureSet.rois.get(
                    event.roiNumber
                );

                if (!sourceRoi) {
                    console.warn(`Source ROI ${event.roiNumber} not found`);
                    return;
                }

                const newName = generateUniqueStructureName(
                    groundTruth.dicomStructureSet,
                    sourceRoi.name
                );

                const { set } = recoilInterface;

                const { newRoi } = repository.createRoi(
                    event.structureSetUid,
                    sourceRoi.interpretedType
                );

                const finalReadModel = repository.updateRoi(
                    event.structureSetUid,
                    newRoi.roiNumber,
                    (roi) => {
                        roi.name = newName;
                        roi.color = generateDarkerColor(sourceRoi.color);
                        roi.visible = sourceRoi.visible;
                        roi.contourThickness = sourceRoi.contourThickness;
                        roi.contours = sourceRoi.contours.map(
                            (contour) =>
                                new DicomContour(
                                    contour.type,
                                    contour.data.map(
                                        (point) => point.slice() as ReadonlyVec3
                                    ),
                                    contour.referencedSOPClassUID,
                                    contour.referencedSOPInstanceUID
                                )
                        );
                        roi.roiDescription = sourceRoi.roiDescription;
                        roi.roiGenerationAlgorithm =
                            sourceRoi.roiGenerationAlgorithm;
                        roi.roiGenerationDescription =
                            sourceRoi.roiGenerationDescription;
                        roi.electronDensityOverride =
                            sourceRoi.electronDensityOverride;
                        roi.massDensityOverride = sourceRoi.massDensityOverride;
                    }
                );

                // Persist the copied contours to the DICOM dataset so
                // they are included when the structure set is exported
                groundTruth.dicomStructureSet.updateContourSequence(
                    newRoi.roiNumber
                );

                set(currentStructureSetState, finalReadModel);
                set(hasUnsavedChangesState, true);

                await domainEventBus.publish(
                    new RoiCreatedEvent(
                        event.structureSetUid,
                        newRoi.roiNumber,
                        event.source
                    )
                );
            } catch (error) {
                console.error(`Failed to handle ROI copy:`, error);
            }
        },
        [structureSetReadModel?.dicomUid, env]
    );

    const handleRoiSdfCopyOverRequest = useRecoilCallback(
        () => async (event: RoiSdfCopyOverRequestedEvent) => {
            try {
                if (!structureSetReadModel?.dicomUid) {
                    console.warn(
                        'No structure set UID available for SDF copy-over'
                    );
                    return;
                }

                if (event.structureSetUid !== structureSetReadModel.dicomUid) {
                    console.warn(
                        `SDF copy-over event for structure set ${event.structureSetUid} ignored`
                    );
                    return;
                }

                if (!env) {
                    console.warn('No environment available for SDF copy-over');
                    return;
                }

                const sourceRoi = env.getRoi(event.sourceRoiNumber);
                const targetRoi = env.getRoi(event.targetRoiNumber);

                if (!sourceRoi || !targetRoi) {
                    console.warn(
                        `Source or target ROI not found for SDF copy-over`
                    );
                    return;
                }

                // Check if target ROI can be modified (includes approval status and other rules)
                if (!targetRoi.canModify) {
                    console.warn(
                        `Target ROI ${event.targetRoiNumber} cannot be modified (may be approved or locked)`
                    );
                    return;
                }

                const sourceTexture = sourceRoi.texture;
                if (!sourceTexture || sourceTexture.isEmpty) {
                    console.warn(
                        `Source ROI ${event.sourceRoiNumber} has no SDF data`
                    );
                    return;
                }

                const targetTexture = targetRoi.texture;
                if (!targetTexture) {
                    console.warn(
                        `Target ROI ${event.targetRoiNumber} has no texture`
                    );
                    return;
                }

                // Capture pre-copy state and publish modification-started event
                // so that the existing handler drives undo initialisation
                const prePixels = targetTexture.pixels.map((slice) =>
                    slice.slice()
                );
                const preGrid = targetTexture.textureGrid;
                await domainEventBus.publish(
                    new RoiContourModificationStartedEvent(
                        event.targetRoiNumber,
                        'copy-over-structure',
                        prePixels,
                        preGrid
                    )
                );

                // Clone source texture and assign to target
                const clonedTexture = sourceTexture.clone();
                targetRoi.texture = clonedTexture;

                // Capture post-copy state and publish contours-modified event
                // so that the existing handler drives undo capture + read model update
                const postPixels = clonedTexture.pixels.map((slice) =>
                    slice.slice()
                );
                const postGrid = clonedTexture.textureGrid;
                await domainEventBus.publish(
                    new RoiContoursModifiedEvent(
                        event.targetRoiNumber,
                        'copy-over-structure',
                        postPixels,
                        postGrid,
                        prePixels,
                        preGrid
                    )
                );

                // Persist the copied contours to the DICOM dataset so
                // they are included when the structure set is exported
                env.saveChanges();
                const repository = getStructureSetRepository();
                const groundTruth = repository.getGroundTruth(
                    event.structureSetUid
                );
                groundTruth.dicomStructureSet.updateContourSequence(
                    event.targetRoiNumber
                );

                // Trigger view update
                env.triggerContourLayerUpdate();
            } catch (error) {
                console.error(`Failed to handle SDF copy-over:`, error);
            }
        },
        [structureSetReadModel?.dicomUid, env]
    );

    useEffect(() => {
        const subscriptions = [
            domainEventBus.subscribe(
                RoiCreationRequestedEvent.EVENT_TYPE,
                handleRoiCreationRequest
            ),
            domainEventBus.subscribe(
                RoiSelectionRequestedEvent.EVENT_TYPE,
                handleRoiSelectionRequest
            ),
            domainEventBus.subscribe(
                RoiDeletionRequestedEvent.EVENT_TYPE,
                handleRoiDeletionRequest
            ),
            domainEventBus.subscribe(
                RoiVisibilityChangeRequestedEvent.EVENT_TYPE,
                handleVisibilityChangeRequest
            ),
            domainEventBus.subscribe(
                RoiNameChangeRequestedEvent.EVENT_TYPE,
                handleNameChangeRequest
            ),
            domainEventBus.subscribe(
                RoiColorChangeRequestedEvent.EVENT_TYPE,
                handleColorChangeRequest
            ),
            domainEventBus.subscribe(
                RoiContourThicknessChangeRequestedEvent.EVENT_TYPE,
                handleContourThicknessChangeRequest
            ),
            domainEventBus.subscribe(
                RoiApprovalStatusChangeRequestedEvent.EVENT_TYPE,
                handleApprovalStatusChangeRequest
            ),
            domainEventBus.subscribe(
                RoiInterpretedTypeChangeRequestedEvent.EVENT_TYPE,
                handleInterpretedTypeChangeRequest
            ),
            domainEventBus.subscribe(
                RoiElectronDensityOverrideChangeRequestedEvent.EVENT_TYPE,
                handleElectronDensityOverrideChangeRequest
            ),
            domainEventBus.subscribe(
                RoiMassDensityOverrideChangeRequestedEvent.EVENT_TYPE,
                handleMassDensityOverrideChangeRequest
            ),
            domainEventBus.subscribe(
                RoiDescriptionChangeRequestedEvent.EVENT_TYPE,
                handleDescriptionChangeRequest
            ),
            domainEventBus.subscribe(
                RoiGenerationAlgorithmChangeRequestedEvent.EVENT_TYPE,
                handleGenerationAlgorithmChangeRequest
            ),
            domainEventBus.subscribe(
                RoiContoursModifiedEvent.EVENT_TYPE,
                handleContoursModified
            ),
            domainEventBus.subscribe(
                RoiPointCoordinatesChangeRequestedEvent.EVENT_TYPE,
                handlePointCoordinatesChangeRequest
            ),
            domainEventBus.subscribe(
                RoiCopyRequestedEvent.EVENT_TYPE,
                handleRoiCopyRequest
            ),
            domainEventBus.subscribe(
                RoiSdfCopyOverRequestedEvent.EVENT_TYPE,
                handleRoiSdfCopyOverRequest
            ),
        ];

        return () => {
            subscriptions.forEach((sub) => sub.unsubscribe());
        };
    }, [
        handleRoiCreationRequest,
        handleRoiDeletionRequest,
        handleVisibilityChangeRequest,
        handleNameChangeRequest,
        handleRoiSelectionRequest,
        handleColorChangeRequest,
        handleContourThicknessChangeRequest,
        handleApprovalStatusChangeRequest,
        handleInterpretedTypeChangeRequest,
        handleElectronDensityOverrideChangeRequest,
        handleMassDensityOverrideChangeRequest,
        handleDescriptionChangeRequest,
        handleGenerationAlgorithmChangeRequest,
        handleContoursModified,
        handlePointCoordinatesChangeRequest,
        handleRoiCopyRequest,
        handleRoiSdfCopyOverRequest,
    ]);
}
